// Logos and photography from handoff/03-design/assets, imported through Vite so they ship with the build.
// Photos are atmosphere only: never caption them with a recipient's name or imply the people shown
// won an award (decision V6).
import logoHorizontalWhite from './logos/logo-horizontal-white.png';
import logoStackedWhite from './logos/logo-stacked-white.png';
import airborneSensorTestTower from './photos/airborne-sensor-test-tower.jpg';
import areteBandOnStage from './photos/arete-band-on-stage-at-benefit-concert.jpg';
import areteFieldTrailer from './photos/arete-branded-field-trailer-passive-sensor-test.jpg';
import colleaguesChattingInAtrium from './photos/colleagues-chatting-in-atrium.jpg';
import employeeInAreteGear from './photos/employee-in-arete-gear-portrait.jpg';
import engineerAtLaptopInAircraft from './photos/engineer-at-laptop-inside-test-aircraft.jpg';
import engineerInServerRack from './photos/engineer-working-in-a-server-rack.jpg';
import engineersAtWhiteboard from './photos/engineers-at-a-whiteboard.jpg';
import engineersAtWorkstation from './photos/engineers-collaborating-at-a-workstation.jpg';
import groupOfEmployeesLaughing from './photos/group-of-employees-sitting-together-laughing.jpg';
import navyMineDetectionAtSea from './photos/navy-mine-detection-testing-at-sea.jpg';
import openSeaWithDolphins from './photos/open-sea-with-dolphins.jpg';
import opticalFieldTesting from './photos/optical-field-testing-outdoors.jpg';
import engineersMonitoringEquipment from './photos/two-engineers-at-lab-monitoring-equipment.jpg';
import engineersAtLidarFieldTest from './photos/two-engineers-at-lidar-field-test.jpg';
import womanEngineerTestingElectronics from './photos/woman-engineer-testing-electronics-in-lab.jpg';
import signInBackgroundVideo from './video/sign-in-background.mp4';

export const logos = {
  horizontalWhite: logoHorizontalWhite,
  stackedWhite: logoStackedWhite,
} as const;

export const photos = {
  airborneSensorTestTower,
  areteBandOnStage,
  areteFieldTrailer,
  colleaguesChattingInAtrium,
  employeeInAreteGear,
  engineerAtLaptopInAircraft,
  engineerInServerRack,
  engineersAtWhiteboard,
  engineersAtWorkstation,
  groupOfEmployeesLaughing,
  navyMineDetectionAtSea,
  openSeaWithDolphins,
  opticalFieldTesting,
  engineersMonitoringEquipment,
  engineersAtLidarFieldTest,
  womanEngineerTestingElectronics,
} as const;

/** One hero tile: up to three photos that cross-fade in turn. */
export type HeroTile = readonly string[];
/** The hero triptych: three tiles, left to right. */
export type HeroPhotos = readonly [HeroTile, HeroTile, HeroTile];

/** People-first set (employee and celebration pages), as on E1. */
export const peoplePhotos: HeroPhotos = [
  [photos.colleaguesChattingInAtrium, photos.employeeInAreteGear, photos.engineersAtWorkstation],
  [
    photos.groupOfEmployeesLaughing,
    photos.engineersAtWhiteboard,
    photos.engineersMonitoringEquipment,
  ],
  [photos.areteBandOnStage, photos.engineersAtLidarFieldTest, photos.navyMineDetectionAtSea],
];

/** Mission-first set (work pages), as on SL1 and X1. */
export const missionPhotos: HeroPhotos = [
  [photos.engineersAtWorkstation, photos.airborneSensorTestTower, photos.engineersAtWhiteboard],
  [photos.engineerAtLaptopInAircraft, photos.opticalFieldTesting, photos.areteFieldTrailer],
  [photos.navyMineDetectionAtSea, photos.engineerInServerRack, photos.openSeaWithDolphins],
];

/** Sign-in splash A: the background video (muted, looping) and its poster frame. */
export const signInVideo = {
  src: signInBackgroundVideo,
  poster: photos.openSeaWithDolphins,
} as const;

/** Sign-in splash B and the welcome backdrop: three columns of the photo wall. */
export const photoWallColumns: readonly [HeroTile, HeroTile, HeroTile] = [
  [
    photos.engineersAtWorkstation,
    photos.groupOfEmployeesLaughing,
    photos.airborneSensorTestTower,
    photos.womanEngineerTestingElectronics,
    photos.navyMineDetectionAtSea,
  ],
  [
    photos.employeeInAreteGear,
    photos.opticalFieldTesting,
    photos.colleaguesChattingInAtrium,
    photos.engineerAtLaptopInAircraft,
    photos.areteBandOnStage,
  ],
  [
    photos.engineersAtLidarFieldTest,
    photos.engineerInServerRack,
    photos.openSeaWithDolphins,
    photos.engineersMonitoringEquipment,
    photos.areteFieldTrailer,
  ],
];

/** Footer strip: every photo once, in a calm order. */
export const footerStripPhotos: readonly string[] = [
  photos.navyMineDetectionAtSea,
  photos.engineersAtWorkstation,
  photos.airborneSensorTestTower,
  photos.colleaguesChattingInAtrium,
  photos.opticalFieldTesting,
  photos.engineerAtLaptopInAircraft,
  photos.groupOfEmployeesLaughing,
  photos.openSeaWithDolphins,
  photos.engineersAtLidarFieldTest,
  photos.areteFieldTrailer,
  photos.engineersAtWhiteboard,
  photos.engineerInServerRack,
];
